/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Form,
  message,
  Modal,
  notification,
  UploadFile,
  UploadProps,
} from 'antd';
import { IMedia, IMediaUpload } from '@/interfaces/media';
import { useMutation } from '@tanstack/react-query';
import { uploadMedia } from '@/adapters/media';
import Dragger from 'antd/es/upload/Dragger';
import DocumentCard from '../card/document';

interface IProps {
  data: Partial<IMediaUpload>;
  open: boolean;
  onUploaded: (id?: number) => void;
  onCancel: () => void;
  ExternalApi?: (data: Partial<IMediaUpload>) => Promise<any>;
}

const MediaUploader = ({
  open,
  onCancel,
  onUploaded,
  data,
  ExternalApi,
}: IProps) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [url, setUrl] = useState<string>('');
  const [form] = Form.useForm<Partial<IMedia>>();
  const mutations = useMutation({
    mutationFn: async (data: Partial<IMediaUpload>) =>
      ExternalApi ? await ExternalApi(data) : await uploadMedia(data),
    mutationKey: ['upload-media'],
    onSuccess: data => {
      if (!data) {
        throw new Error();
      } else {
        notification.success({
          message: 'อัพโหลดสำเร็จ',
        });
        setFileList([]);
        setUrl('');
        onUploaded(data?.data?.id);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IMediaUpload>) => {
        values.file = fileList.length ? (fileList[0] as any) : null;
        values.modelId = data.modelId;
        values.modelType = data.modelType;
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setUrl('');
    },
    beforeUpload: file => {
      const isAllowedType =
        file.type === 'application/pdf' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/png';
      // file.type ===
      //   'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      // file.type ===
      //   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      // file.type ===
      //   'application/vnd.openxmlformats-officedocument.presentationml.presentation';

      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isAllowedType) {
        message.error('You can only upload PDF, JPEG, PNG');
        return false;
      }
      if (!isLt5M) {
        message.error('File must be smaller than 5MB!');
        return false;
      }
      const url = URL.createObjectURL(file);
      setUrl(url);
      setFileList([file]);
      return false;
    },
    fileList: fileList,
    showUploadList: false,
    accept: '.pdf,.jpg,.jpeg,.png,.pdf',
  };

  return (
    <Modal
      title="อัพโหลดเอกสาร"
      open={open}
      onCancel={() => {
        setFileList([]);
        setUrl('');
        onCancel();
      }}
      footer={
        <div className="flex w-full gap-2 justify-center">
          <Button key="back" onClick={onCancel}>
            ยกเลิก
          </Button>
          <Button
            key="submit"
            type="primary"
            form="uploader"
            onClick={handleUpdate}
            disabled={!fileList?.length}
          >
            อัพโหลด
          </Button>
        </div>
      }
      destroyOnClose
    >
      <div className="flex flex-col gap-3 w-full">
        <Form
          form={form}
          name="uploader"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
        >
          <Form.Item
            name="file"
            rules={[
              { required: true, message: 'เลือกไฟล์เอกสารที่ต้องการอัพโหลด' },
            ]}
          >
            <Dragger {...props}>
              <p className="ant-upload-text">
                เลือกไฟล์เอกสารที่ต้องการอัพโหลด
              </p>
              <p className="ant-upload-hint">
                อัพโหลดได้เฉพาะไฟล์ PDF, JPEG, PNG
              </p>
              <p className="ant-upload-hint">ขนาดไม่เกิน 5 MB.</p>
            </Dragger>
          </Form.Item>
        </Form>
        {url && (
          <DocumentCard
            key="uploader"
            type={fileList[0]?.type?.split('/')[1] ?? ''}
            url={url}
            name={fileList[0]?.name ?? ''}
            onAction={() => {
              setUrl('');
              setFileList([]);
            }}
          />
        )}
      </div>
    </Modal>
  );
};

export default MediaUploader;
